/**
 * Cart Subscribe Button
 *
 * Handles the interactions only. Whether the button, the frequency dropdown or the plan name
 * is shown for a line is decided in Liquid by snippets/cart-items.liquid, which already knows
 * both the product's selling plans and the line's current allocation. Applying a plan
 * re-renders the drawer section server-side, so that markup stays authoritative throughout.
 */

(function() {

  // subscribe button click
  function handleSubscribeButtonClick(event) {
    const button = event.target.closest('.cart-item__subscribe-button');
    if (!button) return;

    const variantId = button.dataset.variantId;
    const lineItemKey = button.dataset.lineItemKey;
    const itemId = button.dataset.itemId;
    const totalOptions = parseInt(button.dataset.totalOptions || '0', 10);
    const firstPlanId = button.dataset.firstPlanId;

    const itemRow = button.closest('.cart-item');
    if (!itemRow) return;

    const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
    const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
    const select = optionsDiv?.querySelector('.cart-item__subscribe-frequency-select');
    const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');

    // If only one option, apply it directly without showing dropdown
    if (totalOptions === 1 && firstPlanId) {
      updateCartItemSellingPlan(lineItemKey, firstPlanId);
      return;
    }

    // If more than one option, show the dropdown
    if (totalOptions > 1) {
      // Show options and hide button
      if (buttonWrapper) {
        buttonWrapper.style.display = 'none';
      }

      if (optionsDiv) {
        optionsDiv.style.display = 'flex';
      }

      // Hide subscription name text when dropdown is shown
      if (subscriptionNameText) {
        subscriptionNameText.style.display = 'none';
      }

      // Apply the first/default selling plan option
      if (select && select.options.length > 0) {
        const firstOption = select.options[0];

        if (firstOption.value !== 'unsubscribe') {
          const selectedValue = firstOption.value;
          const parts = selectedValue.split('-');
          const sellingPlanId = parts[0];

          if (sellingPlanId) {
            select.value = selectedValue;
            updateCartItemSellingPlan(lineItemKey, sellingPlanId);
          }
        }
      }
    }
  }

  // /cart/change.js resets the line to quantity 1 when only the selling plan is sent,
  // so read the line's current quantity and send it along.
  function getLineQuantity(lineItemKey) {
    const keyEl = document.querySelector('cart-drawer [data-line-item-key="' + lineItemKey + '"]');
    const row = keyEl ? keyEl.closest('.cart-item') : null;
    const input = row ? row.querySelector('input[name="updates[]"]') : null;
    const qty = input ? parseInt(input.value, 10) : NaN;
    return qty > 0 ? qty : null;
  }

  /**
   * Update cart item with selling plan
   */
  async function updateCartItemSellingPlan(lineItemKey, sellingPlanId) {
    try {
      const cartDrawerContainer = document.querySelector('cart-drawer');
      if (!cartDrawerContainer) {
        console.error('Cart drawer not found');
        return;
      }
			const cartDrawerSection = window.getCartDrawerSectionHandle?.() || 'cart-drawer';

      const changeBody = {
        id: lineItemKey,
        selling_plan: sellingPlanId || '',
				sections: [cartDrawerSection],
        sections_url: window.location.pathname
      };
      const lineQuantity = getLineQuantity(lineItemKey);
      if (lineQuantity) changeBody.quantity = lineQuantity;

      const response = await window.CartApi.change(changeBody);

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const responseText = await response.text();
      const parsedState = JSON.parse(responseText);

      if (parsedState.errors) {
        console.error('Cart update errors:', parsedState.errors);
        return;
      }

      // Update cart drawer with new HTML
			if (parsedState.sections && parsedState.sections[cartDrawerSection]) {
        const parser = new DOMParser();
				const newCartDrawerHTML = parser.parseFromString(parsedState.sections[cartDrawerSection], 'text/html');
        const newCartDrawer = newCartDrawerHTML.querySelector('#CartDrawer');

        if (newCartDrawer && typeof updateCartDrawer === 'function') {
          updateCartDrawer(cartDrawerContainer, newCartDrawer);
        }
      }

      // The drawer was just replaced with freshly rendered markup, which already carries the
      // correct subscribe/frequency state from Liquid. Only the handlers need rebinding.
      setTimeout(attachEventListeners, 100);

    } catch (error) {
      console.error('Error updating cart item selling plan:', error);
    }
  }

  function handleFrequencySelectChange(event) {
    const select = event.target;
    if (!select || !select.classList.contains('cart-item__subscribe-frequency-select')) return;

    const variantId = select.dataset.variantId;
    const lineItemKey = select.dataset.lineItemKey;
    const itemId = select.dataset.itemId;
    const selectedValue = select.value;
    
    const itemRow = select.closest('.cart-item');
    if (!itemRow) return;

    const buttonWrapper = itemRow.querySelector('.cart-item__subscribe-button-wrapper');
    const optionsDiv = itemRow.querySelector('.cart-item__subscribe-options');
    const subscriptionNameText = itemRow.querySelector('.cart-item__subscription-name');
    
    if (selectedValue === 'unsubscribe') {
      updateCartItemSellingPlan(lineItemKey, '');

      if (buttonWrapper) {
        buttonWrapper.style.display = '';
      }

      if (optionsDiv) {
        optionsDiv.style.display = 'none';
      }

      if (subscriptionNameText) {
        subscriptionNameText.style.display = 'none';
      }
    } else {
      const parts = selectedValue.split('-');
      const sellingPlanId = parts[0]; // First part is the plan ID

      if (!sellingPlanId) {
        return;
      }

      updateCartItemSellingPlan(lineItemKey, sellingPlanId);
      
      // dropdown visible -  subscription name hidden
      const totalOptions = parseInt(optionsDiv?.dataset.totalOptions || '0', 10);
      if (totalOptions > 1 && subscriptionNameText) {
        subscriptionNameText.style.display = 'none';
      }
    }
  }

  function attachEventListeners() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const buttons = cartDrawer.querySelectorAll('.cart-item__subscribe-button');
    const selects = cartDrawer.querySelectorAll('.cart-item__subscribe-frequency-select');

    buttons.forEach(button => {
      button.removeEventListener('click', handleSubscribeButtonClick);
      button.addEventListener('click', handleSubscribeButtonClick);
    });

    selects.forEach(select => {
      select.removeEventListener('change', handleFrequencySelectChange);
      select.addEventListener('change', handleFrequencySelectChange);
    });
  }

  // Which state to show is settled in Liquid, so all that is left to do on these events is
  // rebind handlers to whatever markup the drawer is currently holding. Binding is
  // remove-then-add, so covering the same render twice costs nothing.
  function init() {
    attachEventListeners();

    ['cart-drawer:connected', 'cart-drawer:open', 'cart-drawer:render', 'cart-drawer:update'].forEach(
      (name) => {
        document.addEventListener(name, () => {
          setTimeout(attachEventListeners, 100);
        });
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
